import React, { Suspense, lazy } from 'react';
import { Router, Route, Switch } from 'wouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';

import { AuthProvider } from '@/contexts/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { HomePage } from '@/pages/HomePage';
import { PWAInstallBanner, OfflineIndicator } from '@/components/ui/pwa-install-banner';
import './App.css';

// Lazy load non-critical pages for better initial load performance
const ReportPage = lazy(() => import('@/pages/ReportPage').then(m => ({ default: m.ReportPage })));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const AboutPage = lazy(() => import('@/pages/AboutPage').then(m => ({ default: m.AboutPage })));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage').then(m => ({ default: m.DisclaimerPage })));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
          <Router>
            <div className="min-h-screen flex flex-col">
              <Header />
              
              <main className="flex-1">
                <Suspense fallback={<PageLoader />}>
                  <Switch>
                    <Route path="/" component={HomePage} />
                    <Route path="/report" component={ReportPage} />
                    <Route path="/analytics" component={AnalyticsPage} />
                    <Route path="/login" component={LoginPage} />
                    <Route path="/register" component={RegisterPage} />
                    <Route path="/dashboard" component={DashboardPage} />
                    <Route path="/admin" component={AdminPage} />
                    <Route path="/profile" component={ProfilePage} />
                    <Route path="/about" component={AboutPage} />
                    <Route path="/privacy" component={PrivacyPage} />
                    <Route path="/terms" component={TermsPage} />
                    <Route path="/disclaimer" component={DisclaimerPage} />
                    <Route path="/verify-email" component={VerifyEmailPage} />

                    {/* 404 Page */}
                    <Route>
                      <div className="container mx-auto px-4 py-16 text-center">
                        <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
                        <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                        <button 
                          onClick={() => window.location.href = '/'}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                        >
                          Go Home
                        </button>
                      </div>
                    </Route>
                  </Switch>
                </Suspense>
              </main>
              
              <Footer />
            </div>
          </Router>
          
          <Toaster />
          <PWAInstallBanner />
          <OfflineIndicator />
        </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
